import React, { useState } from 'react';
import { format, addDays } from 'date-fns';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const MilestoneTemplates = ({ onTemplateApply, onBack, isMobile = false }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const templates = [
    {
      id: 'software-development',
      name: 'Software Development Project',
      description: 'Standard software development lifecycle milestones',
      category: 'Technology',
      milestones: [
        {
          title: 'Project Planning & Requirements',
          description: 'Define project scope, requirements gathering, and initial planning',
          offsetDays: 7,
          priority: 'high',
          assignedParty: 'Both Parties'
        },
        {
          title: 'System Design & Architecture',
          description: 'Technical design, architecture planning, and system specifications',
          offsetDays: 21,
          priority: 'high',
          assignedParty: 'Vendor'
        },
        {
          title: 'Development Phase 1',
          description: 'Core functionality development and basic feature implementation',
          offsetDays: 60,
          priority: 'medium',
          assignedParty: 'Vendor'
        },
        {
          title: 'Testing & Quality Assurance',
          description: 'Comprehensive testing, bug fixes, and quality validation',
          offsetDays: 90,
          priority: 'high',
          assignedParty: 'Vendor'
        },
        {
          title: 'User Acceptance Testing',
          description: 'Client testing, feedback incorporation, and acceptance validation',
          offsetDays: 105,
          priority: 'high',
          assignedParty: 'Client'
        },
        {
          title: 'Deployment & Go-Live',
          description: 'Production deployment, system launch, and post-launch support',
          offsetDays: 120,
          priority: 'critical',
          assignedParty: 'Both Parties'
        }
      ]
    },
    {
      id: 'consulting-engagement',
      name: 'Consulting Engagement',
      description: 'Professional services and consulting project template',
      category: 'Services',
      milestones: [
        {
          title: 'Engagement Kickoff',
          description: 'Initial stakeholder meeting and project scope confirmation',
          offsetDays: 3,
          priority: 'high',
          assignedParty: 'Both Parties'
        },
        {
          title: 'Current State Assessment',
          description: 'Analysis of existing processes, systems, and organizational structure',
          offsetDays: 14,
          priority: 'medium',
          assignedParty: 'Vendor'
        },
        {
          title: 'Strategy Development',
          description: 'Develop recommendations and strategic implementation plan',
          offsetDays: 35,
          priority: 'high',
          assignedParty: 'Vendor'
        },
        {
          title: 'Implementation Phase 1',
          description: 'Begin implementation of key recommendations and changes',
          offsetDays: 60,
          priority: 'medium',
          assignedParty: 'Both Parties'
        },
        {
          title: 'Progress Review & Adjustment',
          description: 'Mid-project review, progress assessment, and plan adjustments',
          offsetDays: 90,
          priority: 'high',
          assignedParty: 'Both Parties'
        },
        {
          title: 'Final Implementation',
          description: 'Complete remaining implementation tasks and knowledge transfer',
          offsetDays: 120,
          priority: 'high',
          assignedParty: 'Vendor'
        },
        {
          title: 'Project Closure & Evaluation',
          description: 'Final deliverables, project evaluation, and transition planning',
          offsetDays: 135,
          priority: 'medium',
          assignedParty: 'Both Parties'
        }
      ]
    },
    {
      id: 'marketing-campaign',
      name: 'Marketing Campaign',
      description: 'Marketing campaign planning and execution milestones',
      category: 'Marketing',
      milestones: [
        {
          title: 'Campaign Strategy & Planning',
          description: 'Define campaign objectives, target audience, and strategic approach',
          offsetDays: 7,
          priority: 'high',
          assignedParty: 'Vendor'
        },
        {
          title: 'Creative Development',
          description: 'Design and develop campaign creative assets and materials',
          offsetDays: 21,
          priority: 'medium',
          assignedParty: 'Vendor'
        },
        {
          title: 'Campaign Launch Preparation',
          description: 'Final preparations, asset approval, and launch coordination',
          offsetDays: 35,
          priority: 'high',
          assignedParty: 'Both Parties'
        },
        {
          title: 'Campaign Launch',
          description: 'Official campaign launch across all planned channels',
          offsetDays: 42,
          priority: 'critical',
          assignedParty: 'Vendor'
        },
        {
          title: 'Mid-Campaign Optimization',
          description: 'Performance analysis, optimization, and tactical adjustments',
          offsetDays: 63,
          priority: 'medium',
          assignedParty: 'Vendor'
        },
        {
          title: 'Campaign Completion & Analysis',
          description: 'Campaign wrap-up, final reporting, and performance analysis',
          offsetDays: 84,
          priority: 'high',
          assignedParty: 'Vendor'
        }
      ]
    },
    {
      id: 'legal-compliance',
      name: 'Legal Compliance Review',
      description: 'Legal and regulatory compliance assessment template',
      category: 'Legal',
      milestones: [
        {
          title: 'Compliance Scope Definition',
          description: 'Define compliance requirements and assessment scope',
          offsetDays: 5,
          priority: 'high',
          assignedParty: 'Both Parties'
        },
        {
          title: 'Current State Analysis',
          description: 'Review existing policies, procedures, and compliance status',
          offsetDays: 21,
          priority: 'high',
          assignedParty: 'Vendor'
        },
        {
          title: 'Gap Analysis & Risk Assessment',
          description: 'Identify compliance gaps and assess associated risks',
          offsetDays: 42,
          priority: 'high',
          assignedParty: 'Vendor'
        },
        {
          title: 'Remediation Plan Development',
          description: 'Develop action plan to address identified compliance gaps',
          offsetDays: 56,
          priority: 'medium',
          assignedParty: 'Vendor'
        },
        {
          title: 'Implementation & Documentation',
          description: 'Implement remediation measures and update documentation',
          offsetDays: 84,
          priority: 'high',
          assignedParty: 'Client'
        },
        {
          title: 'Final Compliance Validation',
          description: 'Final review and validation of compliance status',
          offsetDays: 98,
          priority: 'critical',
          assignedParty: 'Vendor'
        }
      ]
    }
  ];

  const generateMilestonesFromTemplate = (template) => {
    const today = new Date();
    
    return template?.milestones?.map(milestone => ({
      ...milestone,
      dueDate: format(addDays(today, milestone?.offsetDays), 'yyyy-MM-dd'),
      notificationEnabled: true,
      reminderDays: milestone?.priority === 'critical' ? 3 : 7,
      dependencies: []
    }));
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(selectedTemplate?.id === template?.id ? null : template);
  };

  const handleApplyTemplate = () => {
    if (selectedTemplate) {
      const generatedMilestones = generateMilestonesFromTemplate(selectedTemplate);
      onTemplateApply?.(generatedMilestones);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Technology':
        return 'Monitor';
      case 'Services':
        return 'Users';
      case 'Marketing':
        return 'Megaphone';
      case 'Legal':
        return 'Scale';
      default:
        return 'Folder';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Technology':
        return 'text-accent bg-accent/10';
      case 'Services':
        return 'text-success bg-success/10';
      case 'Marketing':
        return 'text-warning bg-warning/10';
      case 'Legal':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className={`${isMobile ? 'p-4' : 'h-full flex flex-col'}`}>
      {/* Header */}
      <div className={`${isMobile ? 'mb-6' : 'p-6 border-b border-border'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Milestone Templates</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Choose from pre-built milestone templates for common contract types
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            iconName="ArrowLeft"
          >
            Back to Timeline
          </Button>
        </div>
      </div>
      {/* Templates List */}
      <div className={`${isMobile ? '' : 'flex-1 overflow-y-auto p-6'}`}>
        <div className="grid gap-4 md:grid-cols-2">
          {templates?.map(template => (
            <div
              key={template?.id}
              onClick={() => handleTemplateSelect(template)}
              className={`p-4 border rounded-lg cursor-pointer transition-legal-fast hover:shadow-legal-md ${
                selectedTemplate?.id === template?.id
                  ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-accent'
              }`}
            >
              {/* Template Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${getCategoryColor(template?.category)}`}>
                    <Icon name={getCategoryIcon(template?.category)} size={20} />
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground">{template?.name}</h4>
                    <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium mt-1 ${getCategoryColor(template?.category)}`}>
                      <span>{template?.category}</span>
                    </div>
                  </div>
                </div>
                
                {selectedTemplate?.id === template?.id && (
                  <Icon name="Check" size={16} className="text-primary" />
                )}
              </div>

              {/* Template Description */}
              <p className="text-sm text-muted-foreground mb-4">
                {template?.description}
              </p>

              {/* Template Stats */}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1">
                    <Icon name="Calendar" size={12} />
                    <span>{template?.milestones?.length} milestones</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={12} />
                    <span>
                      {Math.max(...template?.milestones?.map(m => m?.offsetDays))} days
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-1">
                  <span>
                    {template?.milestones?.filter(m => m?.priority === 'critical' || m?.priority === 'high')?.length} critical
                  </span>
                </div>
              </div>

              {/* Template Preview (when selected) */}
              {selectedTemplate?.id === template?.id && (
                <div className="mt-4 pt-4 border-t border-border">
                  <h5 className="text-sm font-medium text-foreground mb-2">Milestone Preview:</h5>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {template?.milestones?.slice(0, 4)?.map((milestone, index) => (
                      <div key={index} className="flex items-center space-x-2 text-xs">
                        <div className={`w-2 h-2 rounded-full ${
                          milestone?.priority === 'critical' ? 'bg-error' :
                          milestone?.priority === 'high' ? 'bg-warning' :
                          milestone?.priority === 'medium' ? 'bg-accent' : 'bg-muted-foreground'
                        }`} />
                        <span className="text-muted-foreground truncate">
                          {milestone?.title}
                        </span>
                        <span className="text-muted-foreground ml-auto">
                          +{milestone?.offsetDays}d
                        </span>
                      </div>
                    ))}
                    {template?.milestones?.length > 4 && (
                      <div className="text-xs text-muted-foreground text-center">
                        +{template?.milestones?.length - 4} more milestones
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Empty State */}
        {templates?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Layout" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Templates Available</h3>
            <p className="text-muted-foreground">
              Templates will be added based on common contract types and workflows.
            </p>
          </div>
        )}
      </div>
      {/* Footer */}
      {selectedTemplate && (
        <div className={`${isMobile ? 'mt-6 pt-6 border-t border-border' : 'p-6 border-t border-border bg-muted/30'}`}>
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="font-medium text-foreground">{selectedTemplate?.name}</span>
              <span className="text-muted-foreground ml-2">
                • {selectedTemplate?.milestones?.length} milestones will be added
              </span>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setSelectedTemplate(null)}
              >
                Clear Selection
              </Button>
              
              <Button
                onClick={handleApplyTemplate}
                iconName="Plus"
              >
                Apply Template
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestoneTemplates;